import React, { useState, useEffect, } from 'react';
import { Button, ToggleButton, ButtonGroup } from 'react-bootstrap';
import moment from 'moment';
import 'moment/locale/pt-br';
import ApiGSenha from '../../../../services/ApiGSenha';

// Função auxiliar para calcular distância entre dois pontos usando a fórmula de Haversine (distância em Km)
const calcularDistancia = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Raio da Terra em km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c; // Distância em km
  return distance;
};

// Componente de linha do tempo para o modo histórico
const Timeline = ({
  points,
  medida,
  selectedEquipment,
  equipmentsStatus,
  onPointSelect,
  onPeriodChange,
  availablePeriods = { month: true, year: true, all: true },
  currentPeriod = 'month'  // Nova prop para o período atual
}) => {
  const [currentPointIndex, setCurrentPointIndex] = useState(0);
  const [formattedPoints, setFormattedPoints] = useState([]);
  const [totalDistance, setTotalDistance] = useState(0);
  const [activePeriod, setActivePeriod] = useState(currentPeriod); // Use o período atual como valor inicial

  // Use useEffect para sincronizar o activePeriod quando currentPeriod mudar
  useEffect(() => {
    // Certifique-se de que só ativamos um período disponível
    if (currentPeriod && availablePeriods[currentPeriod]) {
      setActivePeriod(currentPeriod);
    } else {
      // Caso o período atual não esteja disponível, encontre o primeiro disponível
      const firstAvailable = ['month', 'year', 'all'].find(p => availablePeriods[p]);
      if (firstAvailable) {
        setActivePeriod(firstAvailable);
        // Notificar o componente pai sobre a mudança
        if (typeof onPeriodChange === 'function') {
          onPeriodChange(firstAvailable);
        }
      }
    }
  }, [currentPeriod, availablePeriods, onPeriodChange]);

  // Formata os pontos para exibição na linha do tempo
  useEffect(() => {
    if (points && points.length > 0) {
      const formatted = points.map((point, index) => {
        const serialNumber = point.properties.data.serial;
        const equipment = equipmentsStatus.find(item => item.serialnumber === serialNumber);
        const measurement = equipment?.measurement;

        // Tratamento para o campo address que pode ser um objeto
        let addressDisplay = 'Endereço não disponível';
        const addressData = point.properties.data.address;

        if (addressData) {
          if (typeof addressData === 'string') {
            addressDisplay = addressData;
          } else if (typeof addressData === 'object') {
            // Extrair os valores do objeto e formatá-los como string
            const parts = [];
            if (addressData.formatedAddr) parts.push(addressData.formatedAddr);
            if (addressData.city) parts.push(addressData.city);
            if (addressData.state) parts.push(addressData.state);
            if (addressData.country) parts.push(addressData.country);
            addressDisplay = parts.join(', ') || 'Endereço não disponível';
          }
        }

        return {
          id: index,
          title: point.properties.data.nameEquipament || serialNumber,
          serial: serialNumber,
          date: point.properties.data.createdAt
            ? moment(point.properties.data.createdAt).format('DD/MM/YYYY HH:mm')
            : 'Data não disponível',
          rawDate: point.properties.data.createdAt,
          coordinates: point.geometry.coordinates,
          measurement: measurement,
          address: addressDisplay,
          originalPoint: point // Mantém referência ao ponto original
        };
      });

      // Ordena os pontos por data, se disponível
      const sortedPoints = formatted.sort((a, b) => {
        if (!a.rawDate) return 1;
        if (!b.rawDate) return -1;
        return new Date(b.rawDate) - new Date(a.rawDate); // Invertido para mostrar do mais recente para o mais antigo
      });

      setFormattedPoints(sortedPoints);

      // Calcula a distância total percorrida
      let distance = 0;
      if (sortedPoints.length > 1) {
        for (let i = 0; i < sortedPoints.length - 1; i++) {
          const [lon1, lat1] = sortedPoints[i].coordinates;
          const [lon2, lat2] = sortedPoints[i + 1].coordinates;
          distance += calcularDistancia(lat1, lon1, lat2, lon2);
        }
      }
      setTotalDistance(distance);
    }
  }, [points, equipmentsStatus]);

  // Função para mudar o período (mensal/anual)
  const handlePeriodChange = (period) => {
    if (period !== activePeriod) {
      console.log('Timeline: mudando período para', period);
      setActivePeriod(period);

      // Verifica se o callback onPeriodChange foi fornecido
      if (typeof onPeriodChange === 'function') {
        try {
          onPeriodChange(period);
        } catch (error) {
          console.error('Erro ao chamar onPeriodChange:', error);
        }
      } else {
        console.warn('Callback onPeriodChange não está definido ou não é uma função');
      }
    }
  };

  // Função para fechar a Timeline
  const handleClose = () => {
    if (typeof onPeriodChange === 'function') {
      try {
        onPeriodChange("");
      } catch (error) {
        console.error('Erro ao chamar onPeriodChange para fechar:', error);
      }
    }
  };

  const getSafeValue = (obj, path, defaultValue = 'N/D') => {
    if (!obj) return defaultValue;

    if (typeof path === 'string') {
      const keys = path.split('.');
      let value = obj;

      for (const key of keys) {
        if (value === null || value === undefined || typeof value !== 'object') {
          return defaultValue;
        }
        value = value[key];
        if (value === null || value === undefined) {
          return defaultValue;
        }
      }

      // Corrigido para aceitar o valor 0
      return value !== null && value !== undefined ? value : defaultValue;
    }

    // Corrigido para aceitar o valor 0 também aqui
    return obj[path] !== null && obj[path] !== undefined ? obj[path] : defaultValue;
  };

  // Função para fazer o download dos dados em formato XLS
  const handleDownloadExcel = () => {
    console.log("handleDownloadExcel");

    try {
      // Função auxiliar para obter valor com segurança
      const originalData = getSafeValue(points[0], 'properties.data', {});
      const serial = getSafeValue(originalData, 'serial');
      let companies = JSON.parse(localStorage.getItem("units")) || [];
      if (!Array.isArray(companies)) {
        companies = [companies];
      }
      if (companies.length === 0) {
        companies = JSON.parse(localStorage.getItem("cooperative")) || [];
        if (!Array.isArray(companies)) {
          companies = [companies];
        }
      }

      let company = companies.find(company => company.equipments.includes(serial));
      const findCompanyIdBySerial = (serial) => {
        return company ? company.name : null;
      };

      const filter = {
        "filter": {
          "list": [
            serial
          ],
          "request_data": {
            "serialnumber": true,
            "name": true,
            "email": true,
            "updatedAt": true,
            "address": true,
            "lat": true,
            "lng": true,
            "versaosw": true,
          },
          "add_fields": [{
            "serial": serial,
            "Nome do Equipamento": getSafeValue(originalData, 'nameEquipament'),
            'Dias para Ativar': getSafeValue(originalData, 'daystoactivate'),
            'Empresa': findCompanyIdBySerial(serial),
          }],
          "format_names": {
            "name": "Nome",
            "serialnumber": "Serial",
            "updatedAt": "Data",
            "address": "Endereço",
            "lat": "Latitude",
            "lng": "Longitude",
            "versaosw": "Versão Software",
            "email": "Email",
          },
          "all_data": true,
          "excel": true
        }
      }

      ApiGSenha.post("/api/logs/unlockfilter", filter, {
        responseType: 'blob'
      })
        .then((response) => {
          // Cria um blob a partir da resposta
          const url = window.URL.createObjectURL(new Blob([response.data]));
          // Cria um link temporário
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'desbloqueios.xlsx'); // Nome do arquivo
          document.body.appendChild(link);
          link.click();
          // Remove o link após o download
          link.parentNode.removeChild(link);
        })
        .catch((error) => {
          console.error("Erro ao gerar relatório:", error);
          alert("Ocorreu um erro ao gerar o relatório. Por favor, tente novamente.");
        });


    } catch (error) {
      console.error("Erro ao gerar relatório:", error);
      alert("Ocorreu um erro ao gerar o relatório. Por favor, tente novamente.");
    }
  };

  const handlePointClick = (index) => {
    setCurrentPointIndex(index);

    // Chamar onPointSelect ao clicar explicitamente em um ponto
    if (formattedPoints.length > 0 && onPointSelect) {
      const point = formattedPoints[index];
      onPointSelect(point.originalPoint, index);
    }
  };

  if (formattedPoints.length === 0) {
    return (
      <div className="timeline-container" style={{
        position: 'absolute',
        bottom: '15px',
        right: '10px',
        width: '350px',
        backgroundColor: 'rgba(28, 66, 32, 0.80)',
        backdropFilter: 'blur(8px)',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.4)',
        padding: '15px',
        color: 'white',
        textAlign: 'center',
        fontWeight: '500'
      }}>
        Carregando histórico...
      </div>
    );
  }

  const currentPoint = formattedPoints[currentPointIndex];

  return (
    <div className="timeline-container" style={{
      position: 'absolute',
      bottom: '15px',
      right: '10px',
      width: '35%',
      minWidth: '350px',
      maxWidth: '50%',
      backgroundColor: 'rgba(20, 40, 25, 0.8)', // Fundo mais escuro
      backdropFilter: 'blur(10px)',
      borderRadius: '14px',
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.5)', // Sombra mais pronunciada
      overflow: 'hidden',
      zIndex: 1000,
      color: 'white',
      border: '1px solid rgba(143, 173, 21, 0.4)',
      pointerEvents: 'none'  // Ignora eventos de mouse para o container principal
    }}>
      {/* Botão de Fechar - Reposicionado para o canto superior direito */}
      <Button
        variant="link"
        className="position-absolute"
        style={{
          top: '8px',
          right: '8px',
          color: 'white',
          fontSize: '25px',
          lineHeight: '1',
          padding: '0',
          zIndex: 1010,
          opacity: 0.9,
          background: 'rgba(143, 173, 21, 1.0)',
          width: '35px',
          height: '35px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid rgba(0, 0, 0, 0.4)',
          pointerEvents: 'auto'
        }}
        onClick={handleClose}
        title="Fechar histórico"
      >
        <span aria-hidden="true">&times;</span>
      </Button>

      {/* Cabeçalho com título e controles */}
      <div style={{
        background: 'linear-gradient(90deg, rgba(20, 50, 25, 0.9) 0%, rgba(143, 173, 21, 0.4) 100%)', // Gradiente mais escuro
        padding: '12px 15px 12px 15px',
        paddingRight: '45px',
        borderBottom: '1px solid rgba(143, 173, 21, 0.4)',
      }}>
        <div className="d-flex justify-content-between align-items-center">
          <div style={{ width: '100%' }}>
            <h6 className="m-0 text-white" style={{
              fontWeight: '700',
              textShadow: '0px 1px 3px rgba(0,0,0,0.5)', // Sombra mais forte no texto
              fontSize: '1.2rem', // Aumentado o tamanho do texto
              letterSpacing: '0.5px'
            }}>
              Histórico de Registros
              <span style={{
                backgroundColor: '#8fad15',
                padding: '3px 8px',
                borderRadius: '12px',
                fontSize: '0.85rem', // Aumentado o tamanho do texto
                marginLeft: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}>
                {currentPointIndex + 1}/{formattedPoints.length}
              </span>
            </h6>
            <div style={{
              fontSize: '0.9rem', // Aumentado o tamanho do texto
              opacity: 0.95, // Maior opacidade para melhor contraste
              marginTop: '8px',
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                backgroundColor: 'rgba(143, 173, 21, 0.25)',
                padding: '4px 10px', // Aumentado o padding para melhor visualização
                borderRadius: '10px',
                marginRight: '8px',
                marginBottom: '4px'
              }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#a5c924" viewBox="0 0 16 16" style={{ marginRight: '4px' }}>
                  <path d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zM4.5 7.5a.5.5 0 0 1 0 1H2.95a2.5 2.5 0 0 1 0-1h1.55zm4.5 0a.5.5 0 0 1 0 1H6.5a2.5 2.5 0 0 1 0-1h2.5zM12 8a.5.5 0 0 1-.5.5h-1.55a2.5 2.5 0 0 1 0-1H11.5a.5.5 0 0 1 .5.5z" />
                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-8 3a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
                </svg>
                {totalDistance.toFixed(2)} km no total
              </span>

              {/* Seletor de Período */}
              <ButtonGroup size="sm" style={{ marginRight: '8px', pointerEvents: 'auto', marginBottom: '4px' }}>
                <ToggleButton
                  type="radio"
                  variant={activePeriod === 'month' ? "success" : "outline-light"}
                  name="period"
                  value="month"
                  checked={activePeriod === 'month'}
                  disabled={!availablePeriods.month}
                  onMouseDown={() => availablePeriods.month && handlePeriodChange('month')}
                  style={{
                    fontSize: '0.8rem', // Aumentado o tamanho do texto
                    padding: '4px 8px', // Aumentado o padding
                    backgroundColor: activePeriod === 'month' ? 'rgba(28, 66, 32, 0.9)' : 'rgba(143, 173, 21, 0.25)',
                    borderColor: activePeriod === 'month' ? '#1c4220' : 'transparent',
                    opacity: availablePeriods.month ? 1 : 0.4,
                    cursor: availablePeriods.month ? 'pointer' : 'not-allowed',
                    color: '#ffffff', // Texto mais claro
                    fontWeight: activePeriod === 'month' ? '600' : '400'
                  }}
                >
                  Mensal
                </ToggleButton>
                <ToggleButton
                  type="radio"
                  variant={activePeriod === 'year' ? "success" : "outline-light"}
                  name="period"
                  value="year"
                  checked={activePeriod === 'year'}
                  disabled={!availablePeriods.year}
                  onMouseDown={() => availablePeriods.year && handlePeriodChange('year')}
                  style={{
                    fontSize: '0.8rem', // Aumentado o tamanho do texto
                    padding: '4px 8px', // Aumentado o padding
                    backgroundColor: activePeriod === 'year' ? 'rgba(28, 66, 32, 0.9)' : 'rgba(143, 173, 21, 0.25)',
                    borderColor: activePeriod === 'year' ? '#1c4220' : 'transparent',
                    opacity: availablePeriods.year ? 1 : 0.4,
                    cursor: availablePeriods.year ? 'pointer' : 'not-allowed',
                    color: '#ffffff', // Texto mais claro
                    fontWeight: activePeriod === 'year' ? '600' : '400'
                  }}
                >
                  Anual
                </ToggleButton>
                {availablePeriods.all && (
                  <ToggleButton
                    type="radio"
                    variant={activePeriod === 'all' ? "success" : "outline-light"}
                    name="period"
                    value="all"
                    checked={activePeriod === 'all'}
                    onMouseDown={() => handlePeriodChange('all')}
                    style={{
                      fontSize: '0.8rem', // Aumentado o tamanho do texto
                      padding: '4px 8px', // Aumentado o padding
                      backgroundColor: activePeriod === 'all' ? 'rgba(28, 66, 32, 0.9)' : 'rgba(143, 173, 21, 0.25)',
                      borderColor: activePeriod === 'all' ? '#1c4220' : 'transparent',
                      color: '#ffffff', // Texto mais claro
                      fontWeight: activePeriod === 'all' ? '600' : '400'
                    }}
                  >
                    Todos
                  </ToggleButton>
                )}
              </ButtonGroup>

              {/* Botão de Download */}
              <Button
                variant="outline-light"
                size="sm"
                onClick={handleDownloadExcel}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  backgroundColor: 'rgba(143, 173, 21, 0.3)',
                  padding: '4px 12px', // Aumentado o padding
                  borderRadius: '10px',
                  marginLeft: '0',
                  border: 'none',
                  fontSize: '0.85rem', // Aumentado o tamanho do texto
                  color: 'white',
                  pointerEvents: 'auto',
                  fontWeight: '500', // Texto mais visível
                  marginBottom: '4px'
                }}
                title="Baixar dados em Excel"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#a5c924" viewBox="0 0 16 16" style={{ marginRight: '6px' }}>
                  <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                  <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
                </svg>
                Relatório
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Informações do ponto atual */}
      <div style={{ padding: '15px' }}>
        {/* Cards de pontos (anterior, atual e próximo) */}
        <div style={{
          display: 'flex',
          alignItems: 'stretch', // Mudei para 'stretch' para que os itens se estendam verticalmente
          justifyContent: 'space-between',
          gap: '3px', // Reduzido de 10px para 3px para aproximar as setas do card central
          marginBottom: '15px',
          overflow: 'hidden',
          minWidth: 0
        }}>
          {/* Seta para o ponto anterior (se houver) */}
          {currentPointIndex > 0 && (
            <Button
              variant="link"
              onClick={() => handlePointClick(currentPointIndex - 1)}
              style={{
                color: '#FFFFFF',
                backgroundColor: 'rgba(40, 70, 40, 0.7)',
                borderRadius: '20px 0px 0px 20px',
                width: '36px',
                height: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                border: '1px solid rgba(143, 173, 21, 0.4)',
                boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                pointerEvents: 'auto',
                flexShrink: 0,
                transition: 'all 0.2s',
                alignSelf: 'stretch'
              }}
              title={`Ponto anterior (${formattedPoints[currentPointIndex - 1].date})`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                <path fillRule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z" />
              </svg>
            </Button>
          )}

          {/* Ponto atual (sempre visível) */}
          <div style={{
            flex: '1',
            backgroundColor: 'rgba(28, 66, 32, 0.75)',
            borderRadius: '10px',
            padding: '15px',
            border: '1px solid rgba(143, 173, 21, 0.4)',
            boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.15), 0 0 10px rgba(143, 173, 21, 0.3)',
            zIndex: 1,
            minWidth: 0,
            overflow: 'hidden'
          }}>
            <h6 style={{
              color: '#FFFFFF',
              fontWeight: '700',
              fontSize: '1.2rem', // Aumentado de 1.1rem para 1.2rem
              marginBottom: '12px',
              borderBottom: '1px solid rgba(143, 173, 21, 0.4)',
              paddingBottom: '5px',
              textShadow: '0px 1px 2px rgba(0,0,0,0.3)'
            }}>
              {currentPoint.title}
            </h6>

            <div className="d-flex flex-wrap">
              <div style={{ width: '50%', marginBottom: '6px' }}>
                <span style={{ fontSize: '0.9rem', opacity: 0.85, color: '#d0d0d0', fontWeight: '500' }}>Data:</span>
                <p style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '6px', color: '#ffffff' }}>{currentPoint.date}</p>
              </div>
            </div>

            <div className="d-flex flex-wrap">
              <div style={{ width: '50%', marginBottom: '6px' }}>
                <span style={{ fontSize: '0.9rem', opacity: 0.85, color: '#d0d0d0', fontWeight: '500' }}>E-mail:</span>
                <p style={{
                  fontSize: '1rem',
                  fontWeight: '500',
                  marginBottom: '6px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  color: '#ffffff'
                }}>{currentPoint.originalPoint?.properties?.data?.email || 'N/D'}</p>
              </div>

              <div style={{ width: '50%', marginBottom: '6px' }}>
                <span style={{ fontSize: '0.9rem', opacity: 0.85, color: '#d0d0d0', fontWeight: '500' }}>Nome:</span>
                <p style={{
                  fontSize: '1rem',
                  fontWeight: '500',
                  marginBottom: '6px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  color: '#ffffff'
                }}>{currentPoint.originalPoint?.properties?.data?.name || 'N/D'}</p>
              </div>
            </div>

            <div style={{ marginBottom: '6px' }}>
              <span style={{ fontSize: '0.9rem', opacity: 0.85, color: '#d0d0d0', fontWeight: '500' }}>Local:</span>
              <p style={{
                fontSize: '1rem',
                fontWeight: '500',
                marginBottom: '6px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: '#ffffff'
              }}>{currentPoint.address}</p>
            </div>

            <div>
              <span style={{ fontSize: '0.9rem', opacity: 0.85, color: '#d0d0d0', fontWeight: '500' }}>Coordenadas:</span>
              <p style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '0', color: '#ffffff' }}>
                {currentPoint.coordinates[1].toFixed(5)}, {currentPoint.coordinates[0].toFixed(5)}
              </p>
            </div>
          </div>

          {/* Seta para o próximo ponto (se houver) */}
          {currentPointIndex < formattedPoints.length - 1 && (
            <Button
              variant="link"
              onClick={() => handlePointClick(currentPointIndex + 1)}
              style={{
                color: '#FFFFFF',
                backgroundColor: 'rgba(40, 70, 40, 0.7)',
                borderRadius: '0px 20px 20px 0px',
                width: '36px',
                height: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                border: '1px solid rgba(143, 173, 21, 0.4)',
                boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                pointerEvents: 'auto',
                flexShrink: 0,
                transition: 'all 0.2s',
                alignSelf: 'stretch'
              }}
              title={`Próximo ponto (${formattedPoints[currentPointIndex + 1].date})`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z" />
              </svg>
            </Button>
          )}
        </div>

        {/* Legenda dos elementos do mapa */}
        <div style={{
          backgroundColor: 'rgba(143, 173, 21, 0.6)',
          borderRadius: '10px',
          padding: '10px',
          marginBottom: '15px',
          border: '1px solid rgba(5, 5, 5, 0.4)',
          boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.15)'
        }}>
          <h6 style={{
            color: '#FFFFFF',
            fontWeight: '600',
            fontSize: '0.95rem', // Aumentado o tamanho do título da legenda
            marginBottom: '8px',
            borderBottom: '1px solid #1c4220',
            paddingBottom: '3px',
          }}>
            Legenda do Mapa
          </h6>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
            <div className="d-flex align-items-center" style={{ fontSize: '0.85rem' }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: '#1c4220',
                border: '1px solid #a5c924',
                boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
                marginRight: '5px'
              }}></div>
              <span>Ponto Inicial</span>
            </div>
            <div className="d-flex align-items-center" style={{ fontSize: '0.85rem' }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: '#a5c924',
                border: '1px solid #1c4220',
                boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
                marginRight: '5px'
              }}></div>
              <span>Ponto Intermediário</span>
            </div>
            <div className="d-flex align-items-center" style={{ fontSize: '0.85rem' }}>
              <div style={{
                width: '12px',
                height: '12px',
                backgroundColor: '#1c4220',
                border: '1px solid #a5c924',
                boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
                marginRight: '5px'
              }}></div>
              <span>Ponto Final</span>
            </div>
            <div className="d-flex align-items-center" style={{ fontSize: '0.85rem' }}>
              <div style={{
                display: 'inline-block',
                width: 0,
                height: 0,
                borderTop: '5px solid transparent',
                borderBottom: '5px solid transparent',
                borderLeft: '8px solid #1c4220',
                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
                marginRight: '5px'
              }}></div>
              <span>Direção</span>
            </div>
          </div>
        </div>

        {/* Linha de progresso */}
        <div style={{
          height: '6px',
          backgroundColor: 'rgba(40, 40, 40, 0.4)', // Fundo mais escuro
          borderRadius: '3px',
          position: 'relative',
          marginBottom: '12px',
          overflow: 'hidden',
          boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.3)'
        }}>
          <div style={{
            position: 'absolute',
            height: '100%',
            width: `${(currentPointIndex / (formattedPoints.length - 1)) * 100}%`,
            background: 'linear-gradient(90deg, #1c4220 0%, #a5c924 100%)', // Verde mais vibrante
            borderRadius: '3px',
            transition: 'width 0.3s ease-in-out'
          }}></div>
        </div>

        {/* Pontos da timeline */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          marginTop: '10px',
          pointerEvents: 'auto'
        }}>
          {formattedPoints.length <= 10 ? (
            // Mostrar todos os pontos se forem 10 ou menos
            formattedPoints.map((point, index) => (
              <div
                key={index}
                onClick={() => handlePointClick(index)}
                style={{
                  cursor: 'pointer',
                  position: 'relative',
                  zIndex: 2
                }}
                title={`${point.date}`}
              >
                <div style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: index === currentPointIndex ? '#a5c924' : 'rgba(255, 255, 255, 0.6)',
                  border: `2px solid ${index === currentPointIndex ? '#1c4220' : 'rgba(28, 66, 32, 0.6)'}`,
                  boxShadow: index === currentPointIndex ? '0 0 8px rgba(165, 201, 36, 0.8)' : 'none',
                  transition: 'all 0.3s',
                  transform: index === currentPointIndex ? 'scale(1.4)' : 'scale(1)',
                  flexShrink: 0
                }}></div>
              </div>
            ))
          ) : (
            // Mostrar versão simplificada para mais de 10 pontos
            Array.from({ length: 10 }, (_, i) => {
              // Cria 10 pontos distribuídos uniformemente
              const segmentSize = formattedPoints.length / 9;
              const pointIndex = i === 9 ? formattedPoints.length - 1 : Math.floor(i * segmentSize);

              return (
                <div
                  key={i}
                  onClick={() => handlePointClick(pointIndex)}
                  style={{
                    cursor: 'pointer',
                    position: 'relative',
                    zIndex: 2
                  }}
                  title={`${formattedPoints[pointIndex].date}`}
                >
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor:
                      currentPointIndex === pointIndex ? '#a5c924' :
                        currentPointIndex > (i * segmentSize) && currentPointIndex < ((i + 1) * segmentSize) ?
                          'rgba(165, 201, 36, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                    border: `2px solid ${currentPointIndex === pointIndex ? '#1c4220' : 'rgba(28, 66, 32, 0.6)'}`,
                    boxShadow: currentPointIndex === pointIndex ? '0 0 8px rgba(165, 201, 36, 0.8)' : 'none',
                    transition: 'all 0.3s',
                    transform: currentPointIndex === pointIndex ? 'scale(1.4)' : 'scale(1)',
                    flexShrink: 0
                  }}></div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default Timeline;